import React, { createContext, useContext, useReducer, useEffect, useState, ReactNode, useRef } from 'react';
import { AuthService } from '../services';
import type { User, AuthState, LoginCredentials, RegisterData } from '../types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  invalidateSession: (message: string) => void;
  canAccessOrganization: (organizationId: string) => boolean;
  isHRManager: () => boolean;
  getHRManagerOrganizationId: () => string | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token?: string } }
  | { type: 'LOGOUT' }
  | { type: 'SESSION_INVALIDATED'; payload: string }
  | { type: 'INITIALIZE'; payload: { user: User; token?: string } | null };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token || null,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGOUT':
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SESSION_INVALIDATED':
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'INITIALIZE':
      if (action.payload) {
        return {
          ...state,
          user: action.payload.user,
          token: action.payload.token || null,
          isAuthenticated: true,
          loading: false,
        };
      }
      return { ...state, loading: false };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [sessionConflictHandled, setSessionConflictHandled] = useState(false);
  const isInitialized = useRef(false); // ✅ Track if we've initialized
  const validationInProgress = useRef(false); // ✅ Prevent concurrent validations

  // ✅ FIXED: Initialize auth only once on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // ✅ Only initialize once
      if (isInitialized.current) {
         return;
      }

      isInitialized.current = true;

      try {
        const user = AuthService.getUser();
        const token = AuthService.getToken();

        if (user && token) {
          // Validate token with backend
          try {
            validationInProgress.current = true;
            const isValid = await AuthService.validateToken();
            validationInProgress.current = false;
            
            if (!isValid) {
              throw new Error('Token expired');
            }
            
             dispatch({ type: 'INITIALIZE', payload: { user, token } });
          } catch (error) {
            console.error('❌ Token validation failed:', error);
            validationInProgress.current = false;
            // Token is invalid, clear auth data
            await AuthService.logout();
            dispatch({ type: 'INITIALIZE', payload: null });
          }
        } else {
          dispatch({ type: 'INITIALIZE', payload: null });
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        validationInProgress.current = false;
        dispatch({ type: 'INITIALIZE', payload: null });
      }
    };

    initializeAuth();
  }, []); // ✅ Empty array - only run once on mount

  // ✅ FIXED: Session validation with proper checks
  useEffect(() => {
    // ✅ Only set up validation if authenticated
    if (!state.isAuthenticated || !state.token) {
      return;
    }


    const interval = setInterval(async () => {
      // ✅ Skip if validation already in progress
      if (validationInProgress.current) {
        return;
      }

      
      try {
        validationInProgress.current = true;
        const isValid = await AuthService.validateToken();
        validationInProgress.current = false;
        
        if (!isValid && !sessionConflictHandled) {
          setSessionConflictHandled(true);
          if (window.showSessionConflictNotification) {
            window.showSessionConflictNotification('You\'ve been logged out because you logged in from another device');
          }
          dispatch({ type: 'SESSION_INVALIDATED', payload: 'You\'ve been logged out because you logged in from another device' });
        }
      } catch (error: any) {
        console.error('❌ Token validation failed:', error);
        validationInProgress.current = false;
        
        // Only logout for specific session conflict errors
        if (error?.response?.data?.error?.includes('logged in from another device') && !sessionConflictHandled) {
          setSessionConflictHandled(true);
          if (window.showSessionConflictNotification) {
            window.showSessionConflictNotification(error.response.data.error);
          }
          dispatch({ type: 'SESSION_INVALIDATED', payload: error.response.data.error });
        }
      }
    }, 30000); // ✅ Changed to 30 seconds (less aggressive)

    return () => {
      clearInterval(interval);
    };
  }, [state.isAuthenticated, state.token, sessionConflictHandled]); // ✅ Proper dependencies

  // ✅ FIXED: Visibility change validation with proper checks
  useEffect(() => {
    if (!state.isAuthenticated || !state.token) {
      return;
    }

    const handleVisibilityChange = async () => {
      // ✅ Only validate when becoming visible and not already validating
      if (document.visibilityState === 'visible' && !validationInProgress.current && !sessionConflictHandled) {
        
        try {
          validationInProgress.current = true;
          const isValid = await AuthService.validateToken();
          validationInProgress.current = false;
          
          if (!isValid) {
            setSessionConflictHandled(true);
            if (window.showSessionConflictNotification) {
              window.showSessionConflictNotification('You\'ve been logged out because you logged in from another device');
            }
            dispatch({ type: 'SESSION_INVALIDATED', payload: 'You\'ve been logged out because you logged in from another device' });
          }
        } catch (error: any) {
          validationInProgress.current = false;
          if (error?.response?.data?.error?.includes('logged in from another device')) {
            setSessionConflictHandled(true);
            if (window.showSessionConflictNotification) {
              window.showSessionConflictNotification(error.response.data.error);
            }
            dispatch({ type: 'SESSION_INVALIDATED', payload: error.response.data.error });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [state.isAuthenticated, state.token, sessionConflictHandled]); // ✅ Proper dependencies

  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const { user, token } = await AuthService.login(credentials);
      setSessionConflictHandled(false);
      validationInProgress.current = false; // ✅ Reset validation flag
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
    } catch (error) {
      console.error('❌ Login failed:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const { user, token } = await AuthService.register(userData);
      setSessionConflictHandled(false);
      validationInProgress.current = false; // ✅ Reset validation flag
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      validationInProgress.current = false; // ✅ Reset validation flag
      dispatch({ type: 'LOGOUT' });
      
      // Force a hard reload to /login to ensure all state is cleared
      setTimeout(() => {
        window.location.replace('/login');
      }, 100); // Small delay to ensure dispatch completes
    }
  };

  const invalidateSession = (message: string) => {
    validationInProgress.current = false; // ✅ Reset validation flag
    dispatch({ type: 'SESSION_INVALIDATED', payload: message });
  };

  // Helper functions for organization access control
  const canAccessOrganization = (organizationId: string): boolean => {
    if (!state.user) return false;
    
    // Admin and FMO CEO can access all organizations
    if (state.user.role === 'admin' || state.user.role === 'fmo_ceo') {
      return true;
    }
    
    // HR managers can only access their assigned organization
    if (state.user.role === 'hr_manager') {
      return state.user.organizationId === organizationId;
    }
    
    // Other roles don't have organization access
    return false;
  };

  const isHRManager = (): boolean => {
    return state.user?.role === 'hr_manager';
  };

  const getHRManagerOrganizationId = (): string | undefined => {
    if (state.user?.role === 'hr_manager') {
      return state.user.organizationId;
    }
    return undefined;
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    invalidateSession,
    canAccessOrganization,
    isHRManager,
    getHRManagerOrganizationId,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}